import { useState, useRef, useEffect, useCallback, useId } from 'react';
import { DictionaryType } from '@spellion/shared';
import type { Dictionary } from '@spellion/shared';

/**
 * Props for the DictionarySelector component
 */
export interface DictionarySelectorProps {
  /** List of available dictionaries */
  dictionaries: Dictionary[];
  /** ID of the currently selected dictionary */
  selectedId: string;
  /** Callback when selection changes */
  onChange: (id: string) => void;
  /** Whether the selector is disabled */
  disabled?: boolean;
  /** Whether the user has premium access (shows custom dictionaries) */
  isPremium?: boolean;
  /** Current user ID to identify user's own dictionaries */
  userId?: string;
  /** Optional class name for styling */
  className?: string;
}

/**
 * DictionarySelector Component
 *
 * A dropdown/select list for choosing the active dictionary for word searches.
 * Features:
 * - ODS (Official Scrabble Dictionary) selected by default
 * - Admin-created global dictionaries visible to all users
 * - User's own custom dictionaries visible only for Premium users
 * - Premium badge shown next to custom dictionaries section
 * - Accessible with proper ARIA labels
 * - Touch-friendly with 44x44px minimum target sizes on mobile
 *
 * @validates Requirements 4.1, 4.4, 5.1
 */
export function DictionarySelector({
  dictionaries,
  selectedId,
  onChange,
  disabled = false,
  isPremium = false,
  userId,
  className = '',
}: DictionarySelectorProps) {
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const buttonRef = useRef<HTMLButtonElement>(null);
  const listId = useId();

  // Separate dictionaries into categories:
  // 1. System dictionaries (type = SYSTEM)
  // 2. Global custom dictionaries (type = CUSTOM, ownerId = null) - created by admin for all users
  // 3. User's own custom dictionaries (type = CUSTOM, ownerId = userId) - user's personal dictionaries
  const systemDictionaries = dictionaries.filter(d => d.type === DictionaryType.SYSTEM);
  const globalCustomDictionaries = dictionaries.filter(d => d.type === DictionaryType.CUSTOM && d.ownerId === null);
  const userCustomDictionaries = dictionaries.filter(d => d.type === DictionaryType.CUSTOM && d.ownerId !== null && d.ownerId === userId);

  // Find selected dictionary - can be null if no dictionaries available
  const selectedDictionary: Dictionary | null = dictionaries.find(d => d.id === selectedId) 
    ?? dictionaries.find(d => d.isDefault)
    ?? systemDictionaries[0] 
    ?? dictionaries[0]
    ?? null;

  // Close dropdown when clicking outside
  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target as Node) &&
        buttonRef.current &&
        !buttonRef.current.contains(event.target as Node)
      ) {
        setIsOpen(false);
      }
    }

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Close dropdown on Escape key and handle keyboard navigation
  useEffect(() => {
    function handleKeyDown(event: KeyboardEvent) {
      if (event.key === 'Escape' && isOpen) {
        setIsOpen(false);
        buttonRef.current?.focus();
      }
    }

    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [isOpen]);

  const toggleDropdown = useCallback(() => {
    if (!disabled) {
      setIsOpen(prev => !prev);
    }
  }, [disabled]);

  const handleSelect = useCallback(
    (dictionaryId: string) => {
      onChange(dictionaryId);
      setIsOpen(false);
      buttonRef.current?.focus();
    },
    [onChange]
  );

  const handleKeyboardSelect = useCallback(
    (event: React.KeyboardEvent, dictionaryId: string) => {
      if (event.key === 'Enter' || event.key === ' ') {
        event.preventDefault();
        handleSelect(dictionaryId);
      }
    },
    [handleSelect]
  );

  // Get display name for dictionary
  const getDisplayName = (dictionary: Dictionary | null): string => {
    return dictionary?.name ?? 'Aucun dictionnaire';
  };

  // If no dictionaries available, show a disabled state
  const noDictionaries = dictionaries.length === 0;

  return (
    <div className={`relative ${className}`}>
      {/* Label */}
      <label
        id={`${listId}-label`}
        className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1.5"
      >
        Dictionnaire
      </label>

      {/* Dropdown trigger button */}
      <button
        ref={buttonRef}
        type="button"
        onClick={toggleDropdown}
        disabled={disabled || noDictionaries}
        className={`
          w-full flex items-center justify-between gap-2 
          px-3 py-2.5 min-h-touch
          bg-white dark:bg-slate-700 border border-slate-300 dark:border-slate-600 rounded-lg
          text-left text-sm font-medium text-slate-900 dark:text-white
          transition-all duration-150
          ${(disabled || noDictionaries)
            ? 'opacity-50 cursor-not-allowed bg-slate-50 dark:bg-slate-800' 
            : 'hover:border-primary-400 dark:hover:border-primary-500 focus:border-primary-500 focus:ring-2 focus:ring-primary-100 dark:focus:ring-primary-900 cursor-pointer'
          }
        `}
        aria-haspopup="listbox"
        aria-expanded={isOpen}
        aria-labelledby={`${listId}-label`}
        aria-controls={isOpen ? listId : undefined}
      >
        <span className="flex items-center gap-2 truncate">
          <BookIcon className="w-4 h-4 text-slate-500 dark:text-slate-400 flex-shrink-0" />
          <span className={`truncate ${noDictionaries ? 'text-slate-400 dark:text-slate-500 italic' : ''}`}>
            {noDictionaries ? 'Aucun dictionnaire disponible' : getDisplayName(selectedDictionary)}
          </span>
          {selectedDictionary?.type === DictionaryType.CUSTOM && (
            <span className="inline-flex items-center px-1.5 py-0.5 text-xs font-medium text-accent-700 dark:text-accent-300 bg-accent-100 dark:bg-accent-900/40 rounded">
              Perso
            </span>
          )}
        </span>
        <ChevronDownIcon
          className={`w-4 h-4 text-slate-400 flex-shrink-0 transition-transform duration-200 ${
            isOpen ? 'rotate-180' : ''
          }`}
        />
      </button>

      {/* Dropdown menu */}
      {isOpen && (
        <div
          ref={dropdownRef}
          id={listId}
          role="listbox"
          aria-labelledby={`${listId}-label`}
          aria-activedescendant={`${listId}-option-${selectedId}`}
          className="absolute z-50 w-full mt-1 bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-lg shadow-lg animate-scale-in origin-top overflow-hidden"
        >
          {/* System dictionaries section */}
          <div className="py-1">
            <div className="px-3 py-2 text-xs font-semibold text-slate-500 dark:text-slate-400 uppercase tracking-wide">
              Dictionnaires officiels
            </div>
            {systemDictionaries.map(dictionary => (
              <DictionaryOption
                key={dictionary.id}
                dictionary={dictionary}
                isSelected={dictionary.id === selectedId}
                onSelect={handleSelect}
                onKeyDown={handleKeyboardSelect}
                listId={listId}
              />
            ))}
            {/* Global custom dictionaries (admin-created, no owner) - available to all */}
            {globalCustomDictionaries.map(dictionary => (
              <DictionaryOption
                key={dictionary.id}
                dictionary={dictionary}
                isSelected={dictionary.id === selectedId}
                onSelect={handleSelect}
                onKeyDown={handleKeyboardSelect}
                listId={listId}
                hidePersonalBadge
              />
            ))}
          </div>

          {/* User's own custom dictionaries section - Premium only */}
          {isPremium && userCustomDictionaries.length > 0 && (
            <div className="border-t border-slate-100 dark:border-slate-700 py-1">
              <div className="px-3 py-2 flex items-center gap-2">
                <span className="text-xs font-semibold text-slate-500 dark:text-slate-400 uppercase tracking-wide">
                  Mes dictionnaires
                </span>
                <PremiumBadge />
              </div>
              {userCustomDictionaries.map(dictionary => (
                <DictionaryOption
                  key={dictionary.id}
                  dictionary={dictionary}
                  isSelected={dictionary.id === selectedId}
                  onSelect={handleSelect}
                  onKeyDown={handleKeyboardSelect}
                  listId={listId}
                />
              ))}
            </div>
          )}

          {/* Show premium upsell if not premium */}
          {!isPremium && (
            <div className="border-t border-slate-100 dark:border-slate-700 py-2 px-3">
              <div className="flex items-center gap-2 text-xs text-slate-500 dark:text-slate-400">
                <SparkleIcon className="w-3.5 h-3.5 text-accent-500" />
                <span>
                  <span className="font-medium text-accent-600 dark:text-accent-400">Premium</span> : ajoutez vos propres dictionnaires
                </span>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

/**
 * Individual dictionary option in the dropdown
 */
interface DictionaryOptionProps {
  dictionary: Dictionary;
  isSelected: boolean;
  onSelect: (id: string) => void;
  onKeyDown: (event: React.KeyboardEvent, id: string) => void;
  listId: string;
  /** Hide the "Perso" badge for global custom dictionaries */
  hidePersonalBadge?: boolean;
}

function DictionaryOption({
  dictionary,
  isSelected,
  onSelect,
  onKeyDown,
  listId,
  hidePersonalBadge = false,
}: DictionaryOptionProps) {
  return (
    <div
      id={`${listId}-option-${dictionary.id}`}
      role="option"
      aria-selected={isSelected}
      tabIndex={0}
      onClick={() => onSelect(dictionary.id)}
      onKeyDown={e => onKeyDown(e, dictionary.id)}
      className={`
        flex items-center justify-between px-3 py-2.5 min-h-touch cursor-pointer
        transition-colors duration-150
        ${isSelected 
          ? 'bg-primary-50 dark:bg-primary-900/30 text-primary-700 dark:text-primary-400' 
          : 'text-slate-700 dark:text-slate-300 hover:bg-slate-50 dark:hover:bg-slate-700'
        }
      `}
    >
      <div className="flex items-center gap-2 min-w-0">
        <span className="truncate font-medium">{dictionary.name}</span>
        {dictionary.type === DictionaryType.CUSTOM && !hidePersonalBadge && (
          <span className="inline-flex items-center px-1.5 py-0.5 text-xs font-medium text-accent-700 dark:text-accent-300 bg-accent-100 dark:bg-accent-900/40 rounded flex-shrink-0">
            Perso
          </span>
        )}
      </div>
      <div className="flex items-center gap-2 flex-shrink-0 ml-2">
        <span className="text-xs text-slate-400 dark:text-slate-500">
          {formatWordCount(dictionary.wordCount)} mots
        </span>
        {isSelected && (
          <CheckIcon className="w-4 h-4 text-primary-600 dark:text-primary-400" />
        )}
      </div>
    </div>
  );
}

/**
 * Premium badge component
 */
function PremiumBadge() {
  return (
    <span className="inline-flex items-center gap-1 px-1.5 py-0.5 text-xs font-medium text-white bg-gradient-to-r from-accent-500 to-accent-600 rounded">
      <SparkleIcon className="w-3 h-3" />
      Premium
    </span>
  );
}

/**
 * Format word count for display (e.g., 400000 -> "400k")
 */
function formatWordCount(count: number): string {
  if (count >= 1000000) {
    return `${(count / 1000000).toFixed(1)}M`;
  }
  if (count >= 1000) {
    return `${Math.round(count / 1000)}k`;
  }
  return count.toString();
}

// Icon components
function BookIcon({ className }: { className?: string }) {
  return (
    <svg
      className={className}
      fill="none"
      viewBox="0 0 24 24"
      stroke="currentColor"
      strokeWidth={2}
      aria-hidden="true"
    >
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        d="M12 6.042A8.967 8.967 0 006 3.75c-1.052 0-2.062.18-3 .512v14.25A8.987 8.987 0 016 18c2.305 0 4.408.867 6 2.292m0-14.25a8.966 8.966 0 016-2.292c1.052 0 2.062.18 3 .512v14.25A8.987 8.987 0 0018 18a8.967 8.967 0 00-6 2.292m0-14.25v14.25"
      />
    </svg>
  );
}

function ChevronDownIcon({ className }: { className?: string }) {
  return (
    <svg
      className={className}
      fill="none"
      viewBox="0 0 24 24"
      stroke="currentColor"
      strokeWidth={2}
      aria-hidden="true"
    >
      <path strokeLinecap="round" strokeLinejoin="round" d="M19 9l-7 7-7-7" />
    </svg>
  );
}

function CheckIcon({ className }: { className?: string }) {
  return (
    <svg
      className={className}
      fill="none"
      viewBox="0 0 24 24"
      stroke="currentColor"
      strokeWidth={2}
      aria-hidden="true"
    >
      <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
    </svg>
  );
}

function SparkleIcon({ className }: { className?: string }) {
  return (
    <svg
      className={className}
      fill="none"
      viewBox="0 0 24 24"
      stroke="currentColor"
      strokeWidth={2}
      aria-hidden="true"
    >
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        d="M9.813 15.904L9 18.75l-.813-2.846a4.5 4.5 0 00-3.09-3.09L2.25 12l2.846-.813a4.5 4.5 0 003.09-3.09L9 5.25l.813 2.846a4.5 4.5 0 003.09 3.09L15.75 12l-2.846.813a4.5 4.5 0 00-3.09 3.09zM18.259 8.715L18 9.75l-.259-1.035a3.375 3.375 0 00-2.455-2.456L14.25 6l1.036-.259a3.375 3.375 0 002.455-2.456L18 2.25l.259 1.035a3.375 3.375 0 002.456 2.456L21.75 6l-1.035.259a3.375 3.375 0 00-2.456 2.456z"
      />
    </svg>
  );
}
